import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import type { Feature } from "geojson";
import { useCallback, useMemo } from "react";
import Supercluster from "supercluster";
import {
    type AerialImagesList,
    type EquipmentMapList,
    InfrastructureEquipmentListInfraTypeEnum,
    type MapPipelinesListRequest,
    type PipelineMapList,
    type PipelineProductEnum,
    type PipelineTypeEnum,
    type SiteMapList,
} from "../../../apiClient/generated";
import { useHasFlag } from "../../../auth";
import { useMapApiClient } from "../../../hooks";
import { createFeatureCollection } from "../../../utils/geopatialUtils";
import {
    MAP_INFRA_CLUSTERING_LIMIT,
    MAP_ZOOM_SHOW_DETAILS,
} from "../constants";
import type { FilterByAreaDef } from "../hooks";
import {
    EquipmentLayer,
    PipelineV2Layers,
    SiteLayer,
    SiteOverviewLayer,
} from "../layers/infrastructure";
import { RasterLayer } from "../layers/rasters";
import { useStable } from "./areaFilter";
import { useMapDataLoader } from "./dataLoader";
import { useMapData } from "./mapDataAndFilters";
import { useMap } from "./mapState";
import { cleanFilters } from "./utils";

export const usePipelinesOnMap = (
    enabled: boolean,
    pipelineProduct: PipelineProductEnum[],
    pipelineType: PipelineTypeEnum[],
    filterByArea?: FilterByAreaDef,
) => {
    const {
        debounced: { viewState, areaOnScreen },
    } = useMap("mainMap");
    const currentZoom = useMemo(() => viewState?.zoom, [viewState]);
    const apiClient = useMapApiClient();
    const debouncedProduct = useDebounce(pipelineProduct, 400);
    const debouncedPipelineType = useDebounce(pipelineType, 400);
    const debouncedFilterByArea = useDebounce(filterByArea, 400);

    // Retrieve overview data
    const overviewDataLoader = useQuery({
        queryKey: [
            "pipelineOverview",
            debouncedProduct,
            debouncedPipelineType,
            debouncedFilterByArea,
        ],
        queryFn: async () => {
            return await apiClient.mapPipelinesOverviewsRetrieve({
                pipelineProduct:
                    debouncedProduct.length > 0 ? debouncedProduct : undefined,
                pipelineType:
                    debouncedPipelineType.length > 0
                        ? debouncedPipelineType
                        : undefined,
                locationWithin:
                    debouncedFilterByArea &&
                    !debouncedFilterByArea.selectedFilters
                        ? JSON.stringify(debouncedFilterByArea.drawnShape)
                        : undefined,
                locationPreset:
                    debouncedFilterByArea &&
                    debouncedFilterByArea.selectedFilters
                        ? debouncedFilterByArea.selectedFilters
                        : undefined,
                lengthMin: 5000,
            });
        },
        enabled,
        refetchOnWindowFocus: false,
        staleTime: 3600 * 24,
    });

    // Pipeline data
    const veryCoarseDataLoader = useMapDataLoader<PipelineMapList>({
        fetchFn: useCallback(
            (areaToFetch, abortSignal, cursor) => {
                return apiClient.mapPipelinesList(
                    {
                        pipelineProduct: debouncedProduct,
                        pipelineType: debouncedPipelineType,
                        lengthMin: 5000,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            debouncedFilterByArea &&
                            debouncedFilterByArea.selectedFilters
                                ? debouncedFilterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [
                debouncedProduct,
                debouncedPipelineType,
                debouncedFilterByArea,
                apiClient,
            ],
        ),
        zoomToStartFetching: 10,
        enabled,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            debouncedFilterByArea && !debouncedFilterByArea.selectedFilters
                ? debouncedFilterByArea.drawnShape
                : undefined,
    });
    const coarseDataLoader = useMapDataLoader<PipelineMapList>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapPipelinesListRequest>({
                        pipelineProduct: debouncedProduct,
                        pipelineType: debouncedPipelineType,
                    });
                if (shortCircuit) {
                    return { results: [] };
                }
                return await apiClient.mapPipelinesList(
                    {
                        ...cleanFilterValues,
                        lengthMin: 2000,
                        lengthMax: 5000,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            debouncedFilterByArea &&
                            debouncedFilterByArea.selectedFilters
                                ? debouncedFilterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [
                debouncedProduct,
                debouncedPipelineType,
                debouncedFilterByArea,
                apiClient,
            ],
        ),
        zoomToStartFetching: 10,
        enabled: enabled && !veryCoarseDataLoader.loading,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            debouncedFilterByArea && !debouncedFilterByArea.selectedFilters
                ? debouncedFilterByArea.drawnShape
                : undefined,
    });
    const detailsDataLoader = useMapDataLoader<PipelineMapList>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapPipelinesListRequest>({
                        pipelineProduct: debouncedProduct,
                        pipelineType: debouncedPipelineType,
                    });
                if (shortCircuit) {
                    return { results: [] };
                }
                return await apiClient.mapPipelinesList(
                    {
                        ...cleanFilterValues,
                        lengthMax: 2000,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            debouncedFilterByArea &&
                            debouncedFilterByArea.selectedFilters
                                ? debouncedFilterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [
                debouncedProduct,
                debouncedPipelineType,
                debouncedFilterByArea,
                apiClient,
            ],
        ),
        zoomToStartFetching: 13,
        // Always let the coarse data loader run first
        enabled: enabled && !coarseDataLoader.loading,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            debouncedFilterByArea && !debouncedFilterByArea.selectedFilters
                ? debouncedFilterByArea.drawnShape
                : undefined,
    });

    const layers = useMemo(() => {
        return PipelineV2Layers(
            currentZoom >= MAP_ZOOM_SHOW_DETAILS
                ? [
                      ...veryCoarseDataLoader.data,
                      ...coarseDataLoader.data,
                      ...detailsDataLoader.data,
                  ]
                : [...veryCoarseDataLoader.data, ...coarseDataLoader.data],
            overviewDataLoader.data,
            enabled,
            currentZoom,
        );
    }, [
        currentZoom,
        enabled,
        coarseDataLoader.data,
        veryCoarseDataLoader.data,
        detailsDataLoader.data,
        overviewDataLoader.data,
    ]);

    return {
        loading:
            detailsDataLoader.loading ||
            coarseDataLoader.loading ||
            (overviewDataLoader.isLoading && overviewDataLoader.isFetching),
        overviews: enabled ? overviewDataLoader.data : {},
        layers,
    };
};

const TWO_WEEKS_IN_MS = 14 * 24 * 3600 * 1000;

/**
 * Filter aerial images for display. Only the latest 2 weeks of images are
 * kept per site, and when `startDate`/`endDate` are set the images are
 * additionally constrained to that range. Images without a site are always
 * kept.
 */
export const filterAerialImages = (
    aerialImages: AerialImagesList[],
    startDate?: Date,
    endDate?: Date,
): AerialImagesList[] => {
    let images = aerialImages;

    // Constrain to the selected date range, when one is set.
    if (startDate && endDate) {
        images = images.filter(
            (aerialImage) =>
                aerialImage.takenAt >= startDate &&
                aerialImage.takenAt <= endDate,
        );
    }

    // Find the latest image date per site. Images without a site are always kept.
    const latestDateBySite = new Map<string, Date>();
    const imagesWithoutSite: AerialImagesList[] = [];

    for (const image of images) {
        if (!image.site) {
            imagesWithoutSite.push(image);
            continue;
        }
        const latestDate = latestDateBySite.get(image.site);
        if (!latestDate || latestDate < image.takenAt) {
            latestDateBySite.set(image.site, image.takenAt);
        }
    }

    // Keep site images within two weeks of that site's latest image.
    const siteImagesWithinRange: AerialImagesList[] = [];
    for (const image of images) {
        if (!image.site) {
            continue;
        }
        const latestDate = latestDateBySite.get(image.site);
        if (
            latestDate &&
            image.takenAt.getTime() >= latestDate.getTime() - TWO_WEEKS_IN_MS
        ) {
            siteImagesWithinRange.push(image);
        }
    }

    // Sort oldest first so newer images are rendered on top of older ones.
    return [...imagesWithoutSite, ...siteImagesWithinRange].sort(
        (a, b) => a.takenAt.getTime() - b.takenAt.getTime(),
    );
};

/**
 * Infrastructure data: custom hook that loads infrastructure-related
 * data (points, geometries and aerial images) based on filter values.
 *
 * Sites and other equipment are returned separately since they are
 * rendered and fetched in different zoom levels.
 */
export const useInfrastructureOnMap = (
    mapId: string,
    enabled: boolean,
    infraTypes: InfrastructureEquipmentListInfraTypeEnum[],
    filterByArea?: FilterByAreaDef,
    dateRangeFilter?: {
        start?: Date;
        end?: Date;
    },
) => {
    const stableMapId = useStable(mapId);
    const apiClient = useMapApiClient();
    const {
        debounced: { viewState, areaOnScreen },
    } = useMap(stableMapId);
    const { selectedContext } = useMapData(stableMapId);
    const currentZoom = useMemo(() => viewState?.zoom, [viewState]);
    const overviewsEnabled = useHasFlag("enable_overviews");

    // Overviews
    const siteOverviews = useQuery({
        queryKey: ["siteOverviews", filterByArea],
        queryFn: async () => {
            const response = apiClient.mapSitesOverviewsRetrieve({
                boxSize: 1,
                locationPreset: filterByArea?.selectedFilters
                    ? filterByArea.selectedFilters
                    : undefined,
            });
            return response;
        },
        enabled:
            overviewsEnabled &&
            enabled &&
            infraTypes.some((t) => t === "SITE") &&
            currentZoom < 8,
        staleTime: 3600 * 24 * 1000,
        refetchOnWindowFocus: false,
    });

    // Load sites first
    const siteDataLoader = useMapDataLoader<SiteMapList>({
        fetchFn: useCallback(
            (areaToFetch, abortSignal, cursor) => {
                return apiClient.mapSitesList(
                    {
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [apiClient, filterByArea],
        ),
        zoomToStartFetching: 7,
        enabled: enabled,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
    });

    const equipmentDataLoader = useMapDataLoader<EquipmentMapList>({
        fetchFn: useCallback(
            (areaToFetch, abortSignal, cursor) => {
                return apiClient.mapEquipmentList(
                    {
                        equipmentType: [
                            InfrastructureEquipmentListInfraTypeEnum.EquipmentGroup,
                            InfrastructureEquipmentListInfraTypeEnum.Equipment,
                        ],
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [apiClient, filterByArea],
        ),
        zoomToStartFetching: 9,
        enabled: enabled,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
    });

    // Aerial images data first
    const aerialImagesDataLoader = useMapDataLoader<AerialImagesList>({
        fetchFn: useCallback(
            (areaToFetch, abortSignal, cursor) => {
                return apiClient.mapAerialImagesList(
                    {
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        takenAtDataRangeAfter: dateRangeFilter.start
                            ? dateRangeFilter.start
                            : undefined,
                        takenAtDataRangeBefore: dateRangeFilter.end
                            ? dateRangeFilter.end
                            : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [apiClient, filterByArea, dateRangeFilter],
        ),
        zoomToStartFetching: 15,
        enabled: enabled && infraTypes.length > 0,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
        unloadZoomLevel: 14,
    });

    const clusterData = useMemo(() => {
        const index = new Supercluster({ radius: 50, maxZoom: 11 });
        index.load(
            siteDataLoader.data.map(
                (i) =>
                    ({
                        type: "Feature",
                        geometry: i.location,
                        properties: i,
                    }) as any,
            ),
        );
        return index;
    }, [siteDataLoader.data]);

    const clusteredPoints = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }

        if (currentZoom > MAP_INFRA_CLUSTERING_LIMIT) {
            return [];
        }

        // Annotate zoom level in which cluster is broken down.
        return clusterData
            .getClusters([-180, -90, 180, 90], currentZoom)
            .map((i) => {
                if (i.properties.cluster) {
                    i.properties.zoom = clusterData.getClusterExpansionZoom(
                        i.properties.cluster_id,
                    );
                    i.properties.tooltip = "Click to zoom in";
                    i.properties.getSiteIds = (): string[] =>
                        clusterData
                            .getLeaves(i.properties.cluster_id, Infinity)
                            .map((leaf) => leaf.properties.id);
                }
                return i;
            });
    }, [clusterData, currentZoom, areaOnScreen]);

    // Memoize items shown on map based on loaded data,
    // zoom level and filter parameters.
    const infrastructureLayers = useMemo(() => {
        // Handle sites
        let sitesToShow: SiteMapList[] = [];
        if (infraTypes.includes("SITE")) {
            if (currentZoom > MAP_ZOOM_SHOW_DETAILS) {
                sitesToShow = [...siteDataLoader.data];
            } else {
                sitesToShow = [
                    ...siteDataLoader.data.map((i) => ({
                        ...i,
                        shape: undefined,
                    })),
                ];
            }
        }
        let data: Feature[] = createFeatureCollection(sitesToShow).features;
        if (currentZoom < MAP_INFRA_CLUSTERING_LIMIT) {
            data = clusteredPoints;
        }

        // Handle equipment
        let equipmentToShow: EquipmentMapList[] = [];
        if (infraTypes.includes("EQUIPMENT")) {
            equipmentToShow = [...equipmentDataLoader.data];
        }

        return [
            SiteOverviewLayer(
                siteOverviews.data,
                enabled && infraTypes.length > 0,
                currentZoom,
            ),
            SiteLayer(
                data,
                enabled && infraTypes.length > 0,
                currentZoom,
                undefined,
            ),
            EquipmentLayer(
                createFeatureCollection(equipmentToShow).features,
                enabled && infraTypes.length > 0,
                currentZoom,
                undefined,
            ),
        ];
    }, [
        infraTypes,
        enabled,
        siteDataLoader.data,
        clusteredPoints,
        currentZoom,
        equipmentDataLoader.data,
        siteOverviews.data,
    ]);

    // Filter aerial image data. Latest 2 weeks of aerial images
    // are shown, and when `dateRangeToShowData` is set the
    // images are additionally constrained to that range.
    const aerialImageData = useMemo(() => {
        const { start, end } = selectedContext.dateRangeToShowData ?? {};
        return filterAerialImages(
            aerialImagesDataLoader.data || [],
            start ? new Date(start) : undefined,
            end ? new Date(end) : undefined,
        );
    }, [aerialImagesDataLoader.data, selectedContext.dateRangeToShowData]);

    const aerialImageLayers = useMemo(() => {
        return [
            RasterLayer({
                data: aerialImageData,
                name: "aerial_image",
                visible: enabled,
                zoomLevel: currentZoom,
                opacity: 1,
            }),
        ];
    }, [enabled, aerialImageData, currentZoom]);

    // Return data
    return {
        loading:
            equipmentDataLoader.loading ||
            siteDataLoader.loading ||
            aerialImagesDataLoader.loading,
        infrastructureLayers,
        aerialImageLayers,
    };
};
